import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  MarketingNav, MarketingFooter, IntegrationStrip,
  MetricStat, PrimaryCTA, SecondaryCTA, BRAND_BLUE
} from '../components/marketing';

// Condensed sibling to /features. Same visual system, restructured
// (not just truncated) — dense text-first summary, no product mockups,
// big-numeral callouts do the heavy lifting where screenshots would.
// Funnels to /features via "See the full tour" and to mailto: for
// request access, matching the calliq one-pager conversion pattern.

const AT_A_GLANCE = [
  { icon: '💬', line: 'Two-way SMS & MMS in one shared team inbox — every number, every thread.' },
  { icon: '🔀', line: 'Point-and-click routing per number: dedicated, round-robin, least-loaded, or queue.' },
  { icon: '📢', line: 'Personalized text blasts with opt-out and SHAFT enforced automatically.' },
  { icon: '🤖', line: 'AI-drafted replies and dashboard insights, built in.' },
  { icon: '📊', line: 'Response-time analytics, SLA tracking, and scheduled PDF reports.' },
  { icon: '🔗', line: 'Native connectors for Salesforce, Clio, Meevo, ShipStation, Teams, and more — with webhooks for anything else.' },
  { icon: '📱', line: 'Native iOS + Android apps with push notifications.' },
];

const OnePager = () => {
  useEffect(() => {
    document.title = 'SnapText — At a glance';
    let tag = document.querySelector('meta[name="description"]');
    if (!tag) { tag = document.createElement('meta'); tag.setAttribute('name', 'description'); document.head.appendChild(tag); }
    tag.setAttribute('content', 'SnapText at a glance — business SMS platform with shared inbox, routing, blasts, and native CRM integrations.');
  }, []);

  return (
    <div className="min-h-screen bg-white text-slate-900" style={{ fontFamily: 'ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif' }}>
      <MarketingNav variant="onepager" />

      {/* HERO */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-6 text-center">
        <div className="text-xs font-semibold tracking-wider uppercase mb-4" style={{ color: BRAND_BLUE }}>SnapText at a glance</div>
        <h1 className="text-4xl sm:text-5xl font-extrabold text-slate-900 tracking-tight leading-[1.05]" style={{ textWrap: 'balance' }}>
          Business SMS that runs like a real workflow.
        </h1>
        <p className="mt-5 text-lg text-slate-600 leading-relaxed" style={{ textWrap: 'balance' }}>
          Shared inbox, no-code routing, personalized blasts, SHAFT compliance, and native CRM sync — on any number your business already uses.
        </p>
        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          <PrimaryCTA>Request access</PrimaryCTA>
          <SecondaryCTA to="/features">See the full tour</SecondaryCTA>
        </div>
      </section>

      {/* METRIC CALLOUTS */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
          <MetricStat number="1 inbox" label="Your whole team on every number, in one place" />
          <MetricStat number="No IT"   label="Set up your numbers and your team from the app — no engineers or consultants" />
          <MetricStat number="100%"    label="Opt-outs and content rules handled for you, on every send" />
        </div>
      </section>

      {/* AT A GLANCE — 8 lines that summarize the platform */}
      <section className="bg-slate-50 border-y border-slate-200 py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 text-center mb-8 tracking-tight">Everything the platform does, on one page.</h2>
          <ul className="space-y-3">
            {AT_A_GLANCE.map((l, i) => (
              <li key={i} className="flex items-start gap-3 p-4 rounded-lg bg-white border border-slate-200">
                <span className="text-xl">{l.icon}</span>
                <span className="text-sm sm:text-base text-slate-800 leading-relaxed">{l.line}</span>
              </li>
            ))}
          </ul>
          <div className="mt-6 text-center">
            <Link to="/features" className="text-sm font-semibold hover:underline" style={{ color: BRAND_BLUE }}>
              Explore each capability in depth →
            </Link>
          </div>
        </div>
      </section>

      {/* INTEGRATIONS */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <IntegrationStrip label="A growing list of native integrations — webhooks cover the rest" />
        <div className="text-center mt-2">
          <Link to="/features#integrations" className="text-sm font-semibold hover:underline" style={{ color: BRAND_BLUE }}>
            See what each integration actually does →
          </Link>
        </div>
      </section>

      {/* HOW IT WORKS — compressed 4 lines */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 text-center mb-6 tracking-tight">Live in days, with nothing for agents to learn.</h2>
        <ol className="grid grid-cols-1 sm:grid-cols-4 gap-4 counter-reset text-sm">
          {[
            ['Bring a number',    'Port or provision — landlines text-enable too.'],
            ['Invite your team',  'Add agents, group into teams, set roles.'],
            ['Wire automation',   'Auto-reply, VIP, business hours, CRM sync.'],
            ['Measure & iterate', 'Dashboards, PDF reports, AI insights.'],
          ].map(([t, b], i) => (
            <li key={i} className="p-4 rounded-lg border border-slate-200 bg-white">
              <div className="w-7 h-7 rounded-full flex items-center justify-center text-white font-bold text-xs mb-2" style={{ backgroundColor: BRAND_BLUE }}>{i + 1}</div>
              <div className="font-semibold text-slate-900">{t}</div>
              <div className="text-slate-600 mt-1 leading-snug">{b}</div>
            </li>
          ))}
        </ol>
      </section>

      {/* FINAL CTA */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className="rounded-2xl p-10 text-center text-white shadow-lg" style={{ background: `linear-gradient(135deg, ${BRAND_BLUE}, #003D80)` }}>
          <h2 className="text-3xl font-extrabold tracking-tight" style={{ textWrap: 'balance' }}>See SnapText on your own numbers.</h2>
          <p className="mt-3 text-white/85 leading-relaxed max-w-xl mx-auto">Fifteen-minute walk-through — bring your numbers and your team.</p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            <a href="mailto:hello@snapcom.com?subject=SnapText%20-%20Request%20access&body=I'd%20like%20to%20see%20SnapText%20on%20our%20numbers."
               className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-white font-semibold shadow-sm hover:bg-slate-50"
               style={{ color: BRAND_BLUE }}>
              Request access
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </a>
            <Link to="/features" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md border border-white/40 text-white font-semibold hover:bg-white/10">
              See the full tour
            </Link>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
};

export default OnePager;
